/*
 * newsbot - forked from autobrr
 */

import { Link } from "@tanstack/react-router";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  Cog6ToothIcon,
  UserCircleIcon,
  ArrowLeftStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";

import { useAuth } from "@contexts/AuthContext";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";

export const RightNav = () => {
  const { user, profile, signOut, isLoading } = useAuth();

  const logoutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      toast.custom((t) => (
        <Toast type="success" body="You have been logged out. Goodbye!" t={t} />
      ));
    },
    onError: (err) => {
      console.error("logout error", err);
      toast.custom((t) => (
        <Toast type="error" body="Failed to log out" t={t} />
      ));
    },
  });

  // Show nothing while loading auth state
  if (isLoading) {
    return (
      <div className="hidden sm:block">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  // Show login link if not authenticated
  if (!user) {
    return (
      <div className="hidden sm:block">
        <div className="flex items-center space-x-3">
          <Link
            to="/login"
            className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  // Show user menu when authenticated
  const displayName = profile?.display_name || user.email?.split("@")[0] || "User";

  return (
    <div className="hidden sm:block">
      <div className="flex items-center space-x-3">
        <Menu as="div" className="relative">
          {({ open }) => (
            <>
              <MenuButton
                className={classNames(
                  open ? "bg-gray-200 dark:bg-gray-700" : "",
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                )}
              >
                <UserCircleIcon className="h-6 w-6" />
                <span>{displayName}</span>
              </MenuButton>

              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in"
              >
                <MenuItem>
                  {({ focus }) => (
                    <Link
                      to="/settings/account"
                      className={classNames(
                        focus ? "bg-gray-100 dark:bg-gray-700" : "",
                        "flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                      )}
                    >
                      <UserCircleIcon className="h-5 w-5 mr-2" />
                      Account
                    </Link>
                  )}
                </MenuItem>

                <MenuItem>
                  {({ focus }) => (
                    <Link
                      to="/settings"
                      className={classNames(
                        focus ? "bg-gray-100 dark:bg-gray-700" : "",
                        "flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                      )}
                    >
                      <Cog6ToothIcon className="h-5 w-5 mr-2" />
                      Settings
                    </Link>
                  )}
                </MenuItem>

                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={() => logoutMutation.mutate()}
                      disabled={logoutMutation.isPending}
                      className={classNames(
                        focus ? "bg-gray-100 dark:bg-gray-700" : "",
                        "flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                      )}
                    >
                      <ArrowLeftStartOnRectangleIcon className="h-5 w-5 mr-2" />
                      {logoutMutation.isPending ? "Logging out..." : "Log out"}
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </>
          )}
        </Menu>
      </div>
    </div>
  );
};
